// src/components/JSONObjectSorter.js

import React, { useState, useEffect, useRef } from "react";
import {
  FaUpload,
  FaArrowUp,
  FaArrowDown,
  FaMoon,
  FaSun,
  FaFileExport,
} from "react-icons/fa";

/**
 * IMPORTANT: `jsonrepair` exports a named function, not a default.
 * So we must do:   import { jsonrepair } from "jsonrepair";
 */
import { jsonrepair } from "jsonrepair";
import JSON5 from "json5";
import { js_beautify as jsBeautify } from "js-beautify";

/**
 * JSONObjectSorter:
 * - Sorts a JSON array by a given key
 * - Fix JSON using jsonrepair + additional patches
 * - line numbers, dark mode, file upload, etc.
 */
const JSONObjectSorter = () => {
  // ------------------ States ------------------
  const [jsonInput, setJsonInput] = useState("");
  const [sortKey, setSortKey] = useState("");
  const [sortedJSON, setSortedJSON] = useState("");
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // For line numbers
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  // Dark mode
  const [darkMode, setDarkMode] = useState(false);

  // Refs
  const inputAreaRef = useRef(null); 
  const lineNumbersRef = useRef(null); 
  const outputRef = useRef(null);

  // Example data
  const exampleJSON = `[
  { "company": "SFDC247", "name": "Alice",   "age": 25, "city": "New York" },
  { "company": "SFDC247", "name": "Bob",     "age": 35, "city": "Los Angeles" },
  { "company": "SFDC247", "name": "Charlie", "age": 30, "city": "Chicago" }
]`;
  const exampleSortKey = "age";

  // Dark Mode side effect
  useEffect(() => {
    if (darkMode) {
      document.body.style.backgroundColor = "#2d2d2d";
      document.body.style.color = "#f0f0f0";
    } else {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    }
  }, [darkMode]);

  // Keep line numbers updated whenever input changes
  useEffect(() => {
    // We only need to do this if showing line numbers
    if (showLineNumbers) {
      // do nothing special - we can do a check here if needed
    }
  }, [jsonInput, showLineNumbers]);

  /**
   * Attempt to parse line & column out of a syntax error message
   * e.g. "Unexpected token } in JSON at position 176 (line 4 column 16)"
   */
  const extractLineColumn = (message) => {
    const match = message.match(/line\s+(\d+)\s+column\s+(\d+)/i);
    if (match) {
      return { line: match[1], col: match[2] };
    }
    return null;
  };

  // Sorting Logic
  const sortJSON = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        let jsonData;
        // parse input
        try {
          jsonData = JSON.parse(jsonInput);
        } catch (parseErr) {
          const info = extractLineColumn(parseErr.message);
          if (info) {
            setError(
              `Invalid JSON near line ${info.line}, column ${info.col}: ${parseErr.message}`
            );
          } else {
            setError(parseErr.message || "Invalid JSON. Please check your input.");
          }
          setSortedJSON("");
          setCopySuccess(false);
          setProcessing(false);
          return;
        }
        // must be array
        if (!Array.isArray(jsonData)) {
          throw new Error("Input must be a valid JSON array.");
        }
        if (!sortKey.trim()) {
          throw new Error("Sort key is required.");
        }

        // check if key exists
        const keyExists = jsonData.some((item) =>
          Object.prototype.hasOwnProperty.call(item, sortKey)
        );
        if (!keyExists) {
          throw new Error(`The key '${sortKey}' does not exist in the JSON objects.`);
        }

        // perform sort
        const sorted = [...jsonData].sort((a, b) => {
          if (a[sortKey] < b[sortKey]) return -1;
          if (a[sortKey] > b[sortKey]) return 1;
          return 0;
        });
        setSortedJSON(JSON.stringify(sorted, null, 2));
        setError("");
      } catch (err) {
        setSortedJSON("");
        setError(err.message);
      } finally {
        setCopySuccess(false);
        setProcessing(false);
      }
    }, 500);
  };

  // Clear
  const clearInputs = () => {
    setJsonInput("");
    setSortKey("");
    setSortedJSON("");
    setError("");
    setCopySuccess(false);
  };

  // Use Example
  const useExampleData = () => {
    setJsonInput(exampleJSON);
    setSortKey(exampleSortKey);
    setSortedJSON("");
    setError("");
    setCopySuccess(false);
  };

  // Copy
  const handleCopy = () => {
    if (!sortedJSON.trim()) return;
    navigator.clipboard.writeText(sortedJSON);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // File Upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setJsonInput(ev.target.result);
      setError("");
      setCopySuccess(false);
      setProcessing(false);
    };
    reader.readAsText(file);
  };

  // Export sorted JSON
  const handleExport = () => {
    if (!sortedJSON.trim()) {
      alert("No sorted JSON to export!");
      return;
    }
    const blob = new Blob([sortedJSON], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "sorted.json";
    link.click();
  };

  // Preview in new tab
  const handlePreview = () => {
    if (!sortedJSON.trim()) {
      alert("No sorted JSON to preview!");
      return;
    }
    const newWin = window.open("", "_blank", "width=600,height=600");
    newWin.document.write(
      `<pre style="white-space:pre; font-family:monospace;">${sortedJSON
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</pre>`
    );
    newWin.document.close();
  };

  // Validate JSON
  const handleValidate = () => {
    if (!sortedJSON.trim()) {
      alert("No output to validate!");
      return;
    }
    try {
      JSON.parse(sortedJSON);
      alert("JSON is valid!");
    } catch (err) {
      alert("Invalid JSON: " + err.message);
    }
  };

  // Scroll Output
  const scrollOutputTop = () => {
    if (outputRef.current) {
      outputRef.current.scrollTop = 0;
    }
  };
  const scrollOutputBottom = () => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  };

  // Sync Scroll for line numbers
  const syncScroll = () => {
    if (inputAreaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = inputAreaRef.current.scrollTop;
    }
  };

  /* =============== Fix JSON logic ===============
     This is your robust approach using:
     1) jsonrepair
     2) jsBeautify
     3) fixAdditionalJSONIssues
     to handle typical errors. 
     Make sure to install: 
       npm install jsonrepair json5 js-beautify 
  */
  const fixJSON = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        // Step A: Use jsonrepair
        const repaired = jsonrepair(jsonInput);

        // Step B: Beautify the repaired JSON
        const beautified = jsBeautify(repaired, {
          indent_size: 2,
          brace_style: "collapse",
        });

        // Step C: Additional fixes
        const finalJSON = fixAdditionalJSONIssues(beautified);
        if (!finalJSON) {
          throw new Error("Unable to apply additional fixes.");
        }
        setJsonInput(finalJSON);
        setError("");
      } catch (err) {
        setError(
          "Auto-fix failed. Please review input manually. Error: " + err.message
        );
      } finally {
        setProcessing(false);
      }
    }, 500);
  };

  const fixAdditionalJSONIssues = (input) => {
    try {
      let repairedJSON = input;
      // Remove duplicates
      const removeDuplicateKeys = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(removeDuplicateKeys);
        } else if (typeof obj === "object" && obj !== null) {
          return Object.keys(obj).reduce((acc, key) => {
            acc[key] = removeDuplicateKeys(obj[key]);
            return acc;
          }, {});
        }
        return obj;
      };

      // Fix mismatched braces/brackets
      const openBraces = (repairedJSON.match(/{/g) || []).length;
      const closeBraces = (repairedJSON.match(/}/g) || []).length;
      const openBrackets = (repairedJSON.match(/\[/g) || []).length;
      const closeBrackets = (repairedJSON.match(/\]/g) || []).length;
      if (openBraces > closeBraces) {
        repairedJSON += "}".repeat(openBraces - closeBraces);
      } else if (closeBraces > openBraces) {
        repairedJSON = repairedJSON.slice(0, -(closeBraces - openBraces));
      }
      if (openBrackets > closeBrackets) {
        repairedJSON += "]".repeat(openBrackets - closeBrackets);
      } else if (closeBrackets > openBrackets) {
        repairedJSON = repairedJSON.slice(0, -(closeBrackets - openBrackets));
      }

      // Fix True/False/Null
      repairedJSON = repairedJSON.replace(/\b(True|False|Null)\b/g, (m) =>
        m.toLowerCase()
      );
      // Fix extra quotes around keys
      repairedJSON = repairedJSON.replace(
        /([{,]\s*)["']([a-zA-Z_][a-zA-Z0-9_]*)["']\s*:/g,
        "$1$2:"
      );
      // Fix missing colons
      repairedJSON = repairedJSON.replace(/(".*?")\s*(?=["{])/g, "$1: ");
      // Convert numeric strings
      repairedJSON = repairedJSON.replace(/"\b(\d+(\.\d+)?)\b"/g, "$1");

      // Parse with JSON5
      const parsed = JSON5.parse(repairedJSON);
      const cleaned = removeDuplicateKeys(parsed);

      // Re-beautify final
      const final = jsBeautify(JSON.stringify(cleaned), {
        indent_size: 2,
        brace_style: "collapse",
      });
      return final;
    } catch (err) {
      return null;
    }
  };

  // Render input with line numbers
  const renderInputWithLineNumbers = () => {
    const lines = jsonInput.split("\n");
    return (
      <div style={{ position: "relative", display: "flex", height: "300px" }}>
        {showLineNumbers && (
          <div
            ref={lineNumbersRef}
            style={{
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              borderRight: "1px solid #ccc",
              width: "50px",
              textAlign: "right",
              userSelect: "none",
              color: "#6c757d",
              paddingTop: "5px",
            }}
          >
            {lines.map((_, i) => (
              <div key={i} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                {i + 1}
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={inputAreaRef}
          className="form-control"
          value={jsonInput}
          onChange={(e) => {
            setJsonInput(e.target.value);
            setError("");
          }}
          onScroll={syncScroll}
          style={{
            flex: 1,
            resize: "none",
            borderRadius: 0,
            border: "2px solid #ccc",
            overflowY: "auto",
            fontFamily: "monospace",
            paddingLeft: showLineNumbers ? "10px" : "1em",
          }}
          placeholder="Enter JSON array here..."
        />
        {/* Upload icon */}
        <label
          htmlFor="jsonSorterUpload"
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
          }}
        >
          <FaUpload size={20} />
        </label>
        <input
          id="jsonSorterUpload"
          type="file"
          style={{ display: "none" }}
          accept=".json"
          onChange={handleFileUpload}
        />
      </div>
    );
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "900px" }}>
      {/* Dark Mode Toggle */}
      <div style={{ textAlign: "right", marginBottom: "10px" }}>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? <FaSun /> : <FaMoon />} {darkMode ? "Light" : "Dark"} Mode
        </button>
      </div>

      {/* Example Data + Fix JSON */}
      <div className="text-center mb-3">
        {processing && <div className="alert alert-info">Processing...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        <button className="btn btn-info me-2" onClick={useExampleData}>
          Use Example Data
        </button>
        <button className="btn btn-warning me-2" onClick={fixJSON}>
          Fix JSON
        </button>
        <div style={{ fontSize: "0.85rem", color: "#666", marginTop: "5px" }}>
          <em>
            This "Fix JSON" attempts to use <strong>jsonrepair</strong> + 
            additional regex-based fixes. Not guaranteed to fix all errors.
          </em>
        </div>
      </div>

      {/* Input area + Sort Key */}
      <div className="row mb-3">
        <div className="col-md-12">{renderInputWithLineNumbers()}</div>
      </div>

      <div className="mb-3" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <input
          type="text"
          className="form-control"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          placeholder="Enter sort key (e.g., age)"
          style={{
            border: "2px solid #ccc",
            borderRadius: "5px",
            maxWidth: "300px",
          }}
        />
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            marginLeft: "10px",
          }}
        >
          <input
            type="checkbox"
            checked={showLineNumbers}
            onChange={(e) => setShowLineNumbers(e.target.checked)}
          />
          <span>Show Line Numbers</span>
        </label>
      </div>

      {/* Buttons: Sort / Clear */}
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={sortJSON} disabled={processing}>
          {processing ? "Sorting..." : "Sort JSON"}
        </button>
        <button className="btn btn-secondary" onClick={clearInputs}>
          Clear
        </button>
      </div>

      {/* Output */}
      {sortedJSON && (
        <div className="mt-4">
          <h5 className="text-center">Sorted JSON</h5>
          <div
            ref={outputRef}
            style={{
              position: "relative",
              height: "300px",
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              border: "2px solid #ccc",
              borderRadius: "5px",
            }}
          >
            <pre
              style={{
                margin: 0,
                padding: "10px",
                whiteSpace: "pre",
                fontFamily: "monospace",
              }}
            >
              {sortedJSON}
            </pre>
          </div>

          {/* 2 rows of action buttons beneath the output */}
          {/* Row 1: Scroll to Top/Bottom */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button className="btn btn-secondary" onClick={scrollOutputTop}>
              <FaArrowUp /> Scroll to Top
            </button>
            <button className="btn btn-secondary" onClick={scrollOutputBottom}>
              <FaArrowDown /> Scroll to Bottom
            </button>
          </div>

          {/* Row 2: Copy, Export, Preview, Validate */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"}`}
              onClick={handleCopy}
            >
              {copySuccess ? "Copied!" : "Copy Output"}
            </button>
            <button className="btn btn-warning" onClick={handleExport}>
              <FaFileExport /> Export JSON
            </button>
            <button className="btn btn-info" onClick={handlePreview}>
              Preview in New Tab
            </button>
            <button className="btn btn-info" onClick={handleValidate}>
              Validate JSON
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JSONObjectSorter;